import { createTheme } from '@mui/material/styles'

const theme = createTheme({
    colorSchemeSelector: 'data-mui-color-scheme',
    colorSchemes: {
        light: {
            palette: {
                primary: {
                    main: '#2563EB',
                    contrastText: '#fff',
                },
                secondary: {
                    main: '#10B981',
                    contrastText: '#fff',
                },
                background: {
                    default: '#F8F8F4',
                    paper: '#EFEFE9',
                },
                divider: '#E2E2DA',
                text: {
                    primary: '#1A1A1A',
                    secondary: '#737373',
                },
                error: { main: '#DC2626' },
                warning: { main: '#D97706' },
                success: { main: '#16A34A' },
            },
        },
        dark: {
            palette: {
                primary: {
                    main: '#2563EB',
                    contrastText: '#fff',
                },
                secondary: {
                    main: '#10B981',
                    contrastText: '#fff',
                },
                background: {
                    default: '#111110',
                    paper: '#1C1C1A',
                },
                divider: '#2E2E2C',
                text: {
                    primary: '#F0F0EC',
                    secondary: '#8A8A85',
                },
                error: { main: '#EF4444' },
                warning: { main: '#F59E0B' },
                success: { main: '#22C55E' },
            },
        },
    },
    typography: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        h1: { fontSize: '20px', fontWeight: 700, letterSpacing: '-0.3px' },
        h2: { fontSize: '17px', fontWeight: 600, letterSpacing: '-0.2px' },
        body1: { fontSize: '15px', fontWeight: 400, lineHeight: 1.55 },
        body2: { fontSize: '13px', fontWeight: 400 },
        caption: {
            fontSize: '11px',
            fontWeight: 500,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.7px',
        },
        button: { fontSize: '13px', fontWeight: 600 },
    },
    shape: {
        borderRadius: 16,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: `
                #app-inner {
                    background:
                        radial-gradient(ellipse 80% 55% at 12% 4%, rgba(37,99,235,0.08) 0%, transparent 55%),
                        radial-gradient(ellipse 70% 45% at 88% 92%, rgba(16,185,129,0.06) 0%, transparent 55%),
                        var(--mui-palette-background-default);
                }
            `,
        },
        MuiBottomNavigation: {
            styleOverrides: {
                root: ({ theme }) => ({
                    background: `color-mix(in srgb, ${theme.palette.background.default} 90%, transparent)`,
                    backdropFilter: 'blur(20px) saturate(1.5)',
                    WebkitBackdropFilter: 'blur(20px) saturate(1.5)',
                    borderTop: `1px solid color-mix(in srgb, ${theme.palette.divider} 60%, transparent)`,
                    boxShadow: '0 -4px 32px rgba(0,0,0,0.1)',
                }),
            },
        },
        MuiBottomNavigationAction: {
            styleOverrides: {
                root: ({ theme }) => ({
                    'minWidth': 64,
                    '& .MuiBottomNavigationAction-label': {
                        fontSize: '11px',
                    },
                    '&.Mui-selected': {
                        'color': theme.palette.primary.main,
                        '& .MuiBottomNavigationAction-label': {
                            fontWeight: 600,
                            fontSize: '11px',
                        },
                    },
                }),
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 600,
                },
                sizeSmall: {
                    height: 28,
                },
                sizeMedium: {
                    height: 34,
                },
                labelSmall: {
                    padding: '0 10px',
                },
                labelMedium: {
                    padding: '0 10px',
                },
                outlined: ({ theme }) => ({
                    borderColor: theme.palette.divider,
                }),
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 600,
                    textTransform: 'none',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
                outlined: ({ theme }) => ({
                    borderColor: theme.palette.divider,
                    boxShadow: 'none',
                }),
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    fontSize: '13px',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '13px',
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    height: 3,
                    borderRadius: 2,
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderColor: theme.palette.divider,
                }),
            },
        },
    },
})

export default theme
